import { ReactNode } from "react";
import { FULL_VIEWPORT_HEIGHT, textPx } from "shared/constants/zoom";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { ConfigProvider, ThemeConfig } from "antd";
import { colors } from "shared/theme/color";
import { translate } from "shared/languages";

// Shared chrome for the revamped auth pages (2026-06-12-admin-login-prototype):
// Login uses the split editorial layout; ForgetPassword / VerifyOTP /
// ResetPassword use the centered card. All styling comes from the design
// tokens (shared/theme/color + the CSS font vars) — no hardcoded hex.

// Faint ink focus ring — same literal as theme/index.ts controlOutline (no token).
const FOCUS_RING = "rgba(15, 15, 14, 0.08)";
// Resting border for form controls — matches formControlBorder in theme/index.ts.
const INPUT_BORDER = "rgba(15, 15, 14, 0.18)";

/**
 * Scoped AntD theme for auth forms: taller rounded inputs (radius 14 per the
 * prototype) and the navy primary pill button (auth CTAs are blue, unlike the
 * admin shell's ink primaries).
 */
export const authFormTheme: ThemeConfig = {
  token: {
    colorPrimary: colors.navy,
    colorPrimaryHover: colors.navyDark,
    colorPrimaryActive: colors.navyDark,
    borderRadius: 14,
    controlHeight: 48,
    colorBorder: INPUT_BORDER,
    controlOutline: FOCUS_RING,
  },
  components: {
    Button: {
      borderRadius: 9999,
      controlHeight: 50,
      fontWeight: 600,
      primaryShadow: "none",
    },
    Form: {
      labelFontSize: textPx(12.5),
      labelColor: colors.ink2,
      verticalLabelPadding: "0 0 8px",
    },
  },
};

// The "+62" glyph that used to live here is PhoneInput's country picker now
// (shared/components/PhoneInput.tsx, variant="inline"): the same hairline
// treatment, with the country chosen rather than fixed.

/** Form.Item label in the prototype's treatment (12.5px / 600 / ink-2). */
export const FieldLabel = ({ children }: { children: ReactNode }) => (
  <span style={{ fontSize: textPx(12.5), fontWeight: 600, color: colors.ink2 }}>{children}</span>
);

/** 30px display heading (prototype .form h2 / .otp-card h2). */
export const AuthHeading = ({ children }: { children: ReactNode }) => (
  <h2
    style={{
      margin: 0,
      fontFamily: "var(--font-display)",
      fontSize: textPx(30),
      fontWeight: 600,
      letterSpacing: "-0.03em",
      color: colors.ink,
    }}
  >
    {children}
  </h2>
);

/** Secondary line under the heading. */
export const AuthSub = ({
  children,
  marginBottom = 30,
}: {
  children: ReactNode;
  marginBottom?: number;
}) => (
  <div
    style={{
      color: colors.ink2,
      fontSize: textPx(14),
      lineHeight: 1.5,
      marginTop: 8,
      marginBottom,
    }}
  >
    {children}
  </div>
);

/** "← Kembali" link at the top of the card pages. */
const BackLink = ({ onClick }: { onClick: () => void }) => (
  <span
    onClick={onClick}
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      fontSize: textPx(13),
      color: colors.ink2,
      cursor: "pointer",
      marginBottom: 18,
    }}
  >
    <ArrowLeftOutlined style={{ fontSize: textPx(13) }} /> {translate("button.back")}
  </span>
);

/**
 * Centered card layout (prototype .otp-wrap / .otp-card) for the
 * forget-password → OTP → reset flow, on the darker page-outer backdrop.
 */
export const AuthCard = ({ onBack, children }: { onBack?: () => void; children: ReactNode }) => (
  <ConfigProvider theme={authFormTheme}>
    <div
      style={{
        minHeight: FULL_VIEWPORT_HEIGHT,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: colors.pageOuter,
        padding: 24,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 440,
          background: colors.card,
          border: `1px solid ${colors.line}`,
          borderRadius: 20,
          padding: "36px 40px",
          boxShadow: "var(--shadow-hover)",
        }}
      >
        {onBack && <BackLink onClick={onBack} />}
        {children}
      </div>
    </div>
  </ConfigProvider>
);
